import React, { useState, useEffect, useMemo } from 'react';
import { Project, ProjectStage, UserRole, User, Comment, ChecklistItem } from '../types';
import { useApp } from '../store';
import { STAGE_CONFIG } from '../constants';
import { X, CheckCircle, Send, ChevronRight, MessageSquare, History, Clock, User as UserIcon, Save, Edit3, Calendar, Award, CheckCircle2, RotateCcw, UserPlus } from 'lucide-react';
import { format } from 'date-fns';

interface ProjectDetailModalProps {
  project: Project;
  onClose: () => void;
}

const RatingPills = ({ 
  label, 
  value, 
  onChange, 
  disabled 
}: { 
  label: string, 
  value: number, 
  onChange: (val: number) => void, 
  disabled: boolean 
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between px-1">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</label>
        <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-tighter">Score: {value || 0}/10</span>
      </div>
      <div className="flex items-center gap-1 bg-slate-50 p-1.5 rounded-2xl border border-slate-100 shadow-inner">
        {[...Array(10)].map((_, i) => {
          const num = i + 1;
          const isSelected = value === num;
          return (
            <button
              key={num}
              disabled={disabled}
              onClick={() => onChange(num)}
              className={`flex-1 py-2 text-[10px] font-black rounded-xl transition-all ${
                isSelected 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                  : disabled 
                    ? 'text-slate-300 cursor-default' 
                    : 'text-slate-400 hover:bg-white hover:text-indigo-600'
              }`}
            >
              {num}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ProjectCommentsSidebar = ({ project }: { project: Project }) => {
  const { addComment, users, currentUser } = useApp();
  const [commentText, setCommentText] = useState('');

  const handlePost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    addComment(project.id, commentText);
    setCommentText('');
  };

  return (
    <div className="flex flex-col h-full border-l border-slate-200 bg-slate-50/30">
      <div className="px-6 py-5 border-b border-slate-200 bg-white">
        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 uppercase tracking-widest">
          <MessageSquare className="w-4 h-4 text-indigo-500" />
          Internal Communication
        </h4>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {(!project.comments || project.comments.length === 0) ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
            <MessageSquare className="w-10 h-10 mb-2" />
            <p className="text-xs font-medium text-center px-4">Start a discussion with the team.</p>
          </div>
        ) : (
          project.comments.map((comment: Comment) => {
            const author = users.find(u => u.id === comment.userId);
            const isMe = comment.userId === currentUser?.id;
            return (
              <div key={comment.id} className="flex flex-col gap-1.5">
                <div className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                  <img src={author?.avatar} className="w-8 h-8 rounded-full border border-slate-200 shrink-0" alt="" />
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm font-medium leading-relaxed shadow-sm ${
                    isMe ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white text-slate-800 rounded-tl-none border border-slate-200'
                  }`}>
                    {comment.text}
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-11 text-[10px] font-bold text-slate-500 ${isMe ? 'flex-row-reverse' : ''}`}>
                  <span className="uppercase tracking-wider">{author?.name}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-400">{format(new Date(comment.timestamp), 'MMM d, h:mm a')}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="p-4 bg-white border-t border-slate-200">
        <form onSubmit={handlePost} className="relative">
          <textarea 
            rows={2}
            placeholder="Type an update..."
            className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-sm font-medium resize-none shadow-inner"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <button 
            type="submit"
            className="absolute right-2 bottom-3 p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md disabled:opacity-50"
            disabled={!commentText.trim()}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

export const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, onClose }) => {
  const { updateProject, advanceStage, recordQAFeedback, regressToDev, regressToDesign, currentUser, users } = useApp();
  const [activeTab, setActiveTab] = useState<'checklist' | 'details' | 'history'>('checklist');
  const [selectedViewStage, setSelectedViewStage] = useState<ProjectStage>(project.stage);
  const [editScope, setEditScope] = useState(project.scope);
  const [isSaving, setIsSaving] = useState(false);
  const [regressionNote, setRegressionNote] = useState('');

  const isAdmin = (currentUser?.role as string) === UserRole.ADMIN;

  useEffect(() => {
    setEditScope(project.scope);
  }, [project.scope]);

  useEffect(() => {
    setSelectedViewStage(project.stage);
  }, [project.stage]);

  const handleSaveScope = () => {
    setIsSaving(true);
    updateProject(project.id, { scope: editScope });
    setTimeout(() => {
        setIsSaving(false);
        onClose(); 
    }, 600);
  };

  const handleAssignment = (role: UserRole, userId: string) => {
    // Cast to string to prevent incorrect TS narrowing
    if (!isAdmin) return;
    const update: any = {};
    if (role === UserRole.DESIGNER) update.assignedDesignerId = userId;
    if (role === UserRole.DEV_MANAGER) update.assignedDevManagerId = userId;
    if (role === UserRole.QA_ENGINEER) update.assignedQAId = userId;
    updateProject(project.id, update);
    onClose(); 
  };

  const getChecklistForStage = (stage: ProjectStage) => {
    switch (stage) {
      case ProjectStage.UPCOMING: return { items: [] as ChecklistItem[], key: null };
      case ProjectStage.DESIGN: return { items: project.designChecklist, key: 'designChecklist' as const };
      case ProjectStage.DEVELOPMENT: return { items: project.devChecklist, key: 'devChecklist' as const };
      case ProjectStage.QA: return { items: project.qaChecklist, key: 'qaChecklist' as const };
      case ProjectStage.SEND_TO_CLIENT:
      case ProjectStage.SENT_TO_CLIENT:
      case ProjectStage.COMPLETED:
        return { items: project.finalChecklist, key: 'finalChecklist' as const };
      default: return { items: [] as ChecklistItem[], key: null };
    }
  };

  const { items, key } = getChecklistForStage(selectedViewStage);
  const isChecklistComplete = items.length > 0 && items.every(i => i.completed);
  
  // Refactored with string casting to ensure comparison works regardless of TS inference bugs
  const canModifyChecklist = useMemo(() => {
    if (!currentUser || (selectedViewStage as string) !== (project.stage as string)) return false;
    
    if (isAdmin) return true;
    
    const role = currentUser.role as string;
    if (project.stage === ProjectStage.DESIGN) {
      return role === UserRole.DESIGNER && currentUser.id === project.assignedDesignerId;
    }
    if (project.stage === ProjectStage.DEVELOPMENT) {
      return role === UserRole.DEV_MANAGER && currentUser.id === project.assignedDevManagerId;
    }
    if (project.stage === ProjectStage.QA) {
      return role === UserRole.QA_ENGINEER && currentUser.id === project.assignedQAId;
    }
    return false;
  }, [currentUser, project, selectedViewStage, isAdmin]);

  const toggleChecklistItem = (itemId: string) => {
    if (!canModifyChecklist || !key) return;
    const newItems = items.map(i => i.id === itemId ? { ...i, completed: !i.completed } : i);
    updateProject(project.id, { [key]: newItems });
  };

  const handleRatingChange = (field: string, val: number) => {
    if (!isAdmin) return;
    updateProject(project.id, { [field]: val });
  };

  const handleAdvanceAction = () => {
    if (!currentUser) return;
    const currentStage = project.stage;
    
    if (currentStage === ProjectStage.DESIGN) advanceStage(project.id, ProjectStage.DEVELOPMENT, currentUser.id);
    else if (currentStage === ProjectStage.DEVELOPMENT) advanceStage(project.id, ProjectStage.QA, currentUser.id);
    else if (currentStage === ProjectStage.QA) recordQAFeedback(project.id, true, currentUser.id);
    else if (currentStage === ProjectStage.SEND_TO_CLIENT) advanceStage(project.id, ProjectStage.SENT_TO_CLIENT, currentUser.id);
    else if (currentStage === ProjectStage.SENT_TO_CLIENT) {
        advanceStage(project.id, ProjectStage.COMPLETED, currentUser.id);
        onClose(); 
    }
  };

  const handleRegressAction = () => {
    if (!isAdmin) return;
    if (!currentUser) return;
    regressToDev(project.id, currentUser.id);
    onClose(); 
  };

  const handleDevToDesignRegression = () => {
    if (!currentUser || !regressionNote.trim()) return;
    regressToDesign(project.id, currentUser.id, regressionNote);
    setRegressionNote('');
    onClose();
  };

  const getCurrentMilestoneDeadline = () => {
    if (project.stage === ProjectStage.DESIGN && project.designDeadline) return { label: 'Design Target', date: project.designDeadline, color: 'text-indigo-600' };
    if (project.stage === ProjectStage.DEVELOPMENT && project.devDeadline) return { label: 'Dev Target', date: project.devDeadline, color: 'text-blue-600' };
    return { label: 'Final Delivery', date: project.overallDeadline, color: 'text-slate-900' };
  };

  const currentMilestone = getCurrentMilestoneDeadline();
  
  const stages: ProjectStage[] = [
    ProjectStage.UPCOMING,
    ProjectStage.DESIGN,
    ProjectStage.DEVELOPMENT,
    ProjectStage.QA,
    ProjectStage.SEND_TO_CLIENT,
    ProjectStage.SENT_TO_CLIENT,
    ProjectStage.COMPLETED
  ];
  
  const getStageIndex = (s: ProjectStage) => stages.indexOf(s);
  const currentStageIdx = getStageIndex(project.stage);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-7xl rounded-3xl shadow-2xl flex flex-row max-h-[92vh] overflow-hidden">
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-10 py-7 border-b border-slate-100 flex items-center justify-between bg-white relative">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <span className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm ${STAGE_CONFIG[project.stage as ProjectStage].color}`}>
                  {STAGE_CONFIG[project.stage as ProjectStage].label}
                </span>
                <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase italic truncate">{project.name}</h2>
              </div>
              <div className="flex items-center gap-4 text-xs font-bold">
                 <p className="text-slate-500">Client: <span className="text-indigo-600 uppercase tracking-widest">{project.clientName}</span></p>
                 <span className="text-slate-200">|</span>
                 <div className="flex items-center gap-2">
                   <Clock className={`w-3.5 h-3.5 ${currentMilestone.color}`} />
                   <span className={currentMilestone.color}>{currentMilestone.label}: {format(new Date(currentMilestone.date), 'MMM d, yyyy')}</span>
                 </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 shrink-0 ml-4">
               <div className="bg-slate-100 p-1 rounded-xl flex gap-1 mr-4 shadow-inner">
                 {[
                   { id: 'checklist', label: 'Workflow', icon: <CheckCircle className="w-4 h-4" /> },
                   { id: 'details', label: 'Project Brief', icon: <Edit3 className="w-4 h-4" /> },
                   { id: 'history', label: 'History', icon: <History className="w-4 h-4" /> }
                 ].map(tab => (
                   <button
                     key={tab.id}
                     onClick={() => setActiveTab(tab.id as any)}
                     className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center gap-2 transition-all ${
                       activeTab === tab.id ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                     }`}
                   >
                     {tab.icon}
                     {tab.label}
                   </button>
                 ))}
               </div>
               <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"><X className="w-6 h-6" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-10 bg-slate-50/20">
            {activeTab === 'details' && (
              <div className="grid grid-cols-12 gap-8 animate-in fade-in zoom-in-95 duration-300">
                <div className="col-span-8 space-y-6">
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/20 overflow-hidden flex flex-col">
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Scope & Requirements</h3>
                      <button onClick={handleSaveScope} disabled={isSaving} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isSaving ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                        {isSaving ? <CheckCircle className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                        {isSaving ? 'Changes Saved' : 'Update & Close'}
                      </button>
                    </div>
                    <textarea value={editScope} onChange={(e) => setEditScope(e.target.value)} className="w-full min-h-[500px] p-8 text-slate-700 leading-relaxed font-medium focus:outline-none resize-none border-none bg-white placeholder:text-slate-300" placeholder="Describe project objectives..." />
                  </div>
                </div>

                <div className="col-span-4 space-y-6">
                  <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2 border-b border-slate-50 pb-3"><Calendar className="w-3 h-3" /> Production Milestones</h4>
                    <div className="space-y-5">
                      <div className="flex justify-between items-center group">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Design Target</span>
                        <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-lg">
                          {project.designDeadline ? format(new Date(project.designDeadline), 'MMM d, yyyy') : 'NOT SET'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center group">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Dev Target</span>
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                          {project.devDeadline ? format(new Date(project.devDeadline), 'MMM d, yyyy') : 'NOT SET'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center group pt-2 border-t border-slate-50">
                        <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Master Deadline</span>
                        <span className="text-xs font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-lg">
                          {format(new Date(project.overallDeadline), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-50 pb-3"><UserPlus className="w-3 h-3" /> Command Center</h4>
                    <div className="space-y-4">
                      {[
                        { role: UserRole.DESIGNER, label: 'Lead Designer', assignedId: project.assignedDesignerId },
                        { role: UserRole.DEV_MANAGER, label: 'Dev Manager', assignedId: project.assignedDevManagerId },
                        { role: UserRole.QA_ENGINEER, label: 'QA Engineer', assignedId: project.assignedQAId }
                      ].map(assign => {
                        const user = users.find(u => u.id === assign.assignedId);
                        return (
                          <div key={assign.role} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl hover:bg-white hover:border-indigo-200 transition-all">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{assign.label}</p>
                            <div className="flex items-center gap-3">
                              {user ? <img src={user.avatar} className="w-8 h-8 rounded-full border border-slate-200" alt="" /> : <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-400"><UserIcon className="w-4 h-4" /></div>}
                              <div className="flex-1 min-w-0">
                                {isAdmin ? (
                                  <select value={assign.assignedId || ""} onChange={(e) => handleAssignment(assign.role, e.target.value)} className="w-full text-xs font-bold text-indigo-600 bg-transparent border-none p-0 focus:ring-0 cursor-pointer">
                                    <option value="">Assign Personnel</option>
                                    {users.filter(u => u.role === assign.role).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                  </select>
                                ) : <p className="text-xs font-bold text-slate-900 truncate">{user?.name || 'Unassigned'}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'checklist' && (
              <div className="max-w-4xl mx-auto py-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="mb-8 bg-white rounded-[2rem] border border-slate-200 p-2 shadow-sm flex items-center overflow-x-auto no-scrollbar">
                  {stages.filter(s => s !== ProjectStage.UPCOMING).map((stage) => {
                    const isActive = stage === project.stage;
                    const isCompleted = getStageIndex(stage) < currentStageIdx;
                    const isSelected = stage === selectedViewStage;
                    
                    return (
                      <button 
                        key={stage}
                        onClick={() => setSelectedViewStage(stage as ProjectStage)}
                        className={`relative flex-1 min-w-[120px] flex flex-col items-center gap-2 py-3 px-4 rounded-2xl transition-all group ${
                          isSelected ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-center relative">
                          {isCompleted ? (
                            <CheckCircle2 className={`w-5 h-5 ${isSelected ? 'text-indigo-200' : 'text-emerald-500'}`} />
                          ) : isActive ? (
                            <div className={`w-5 h-5 rounded-full border-2 ${isSelected ? 'border-indigo-200' : 'border-indigo-600'} flex items-center justify-center`}>
                              <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-white' : 'bg-indigo-600'} animate-pulse`}></div>
                            </div>
                          ) : (
                            <Clock className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-center leading-tight">
                          {STAGE_CONFIG[stage as ProjectStage].label}
                        </span>
                        {isActive && !isSelected && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white shadow-sm"></div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {selectedViewStage === ProjectStage.DESIGN && (
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm mb-10 space-y-10 animate-in slide-in-from-top-4 duration-500">
                    <div className="flex items-center justify-between border-b border-slate-50 pb-5">
                      <div>
                        <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2 uppercase italic">
                          Designer Performance
                        </h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">this project performance score</p>
                      </div>
                      <Award className="w-8 h-8 text-indigo-100" />
                    </div>
                    
                    <div className="space-y-8">
                      <RatingPills label="Design Quality & Aesthetic" value={project.designQualityRating || 0} onChange={(val) => handleRatingChange('designQualityRating', val)} disabled={!isAdmin} />
                      <RatingPills label="Content Integrity & QA" value={project.designContentQARating || 0} onChange={(val) => handleRatingChange('designContentQARating', val)} disabled={!isAdmin} />
                      <RatingPills label="Deadline Accuracy & Speed" value={project.designDeadlineRating || 0} onChange={(val) => handleRatingChange('designDeadlineRating', val)} disabled={!isAdmin} />
                    </div>
                  </div>
                )}

                {project.stage === ProjectStage.UPCOMING ? (
                  <div className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-slate-200">
                     <Clock className="w-16 h-16 text-slate-200 mx-auto mb-6" />
                     <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tight uppercase italic">Awaiting Kickoff</h3>
                     {isAdmin && <button onClick={() => advanceStage(project.id, ProjectStage.DESIGN, currentUser?.id || '')} className="mt-8 px-12 py-5 bg-indigo-600 text-white font-black rounded-2xl shadow-xl hover:-translate-y-1 transition-all flex items-center gap-3 mx-auto uppercase tracking-widest text-xs">Start Project <ChevronRight className="w-5 h-5" /></button>}
                  </div>
                ) : (selectedViewStage as string) === (ProjectStage.COMPLETED as string) ? (
                  <div className="text-center py-20 bg-emerald-50 rounded-[40px] border-2 border-emerald-100">
                     <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
                     <h3 className="text-3xl font-black text-emerald-900 mb-3 tracking-tight uppercase italic">Project Completed</h3>
                  </div>
                ) : (
                  <div className="space-y-10">
                    <div className="flex items-end justify-between border-b border-slate-200 pb-8">
                      <div>
                         <h3 className="text-2xl font-black text-slate-900 flex items-center gap-3 tracking-tighter italic">
                           {STAGE_CONFIG[selectedViewStage as ProjectStage].label} Flow
                         </h3>
                      </div>
                      <div className="flex items-center gap-4 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
                         <div className="w-40 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                           <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${(items.filter(i => i.completed).length / (items.length || 1)) * 100}%` }}></div>
                         </div>
                         <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
                           {items.filter(i => i.completed).length} / {items.length} Tasked
                         </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {items.map(item => (
                        <label key={item.id} className={`flex items-center gap-5 p-6 rounded-3xl border-2 transition-all cursor-pointer group shadow-sm ${item.completed ? 'bg-emerald-50/50 border-emerald-100 text-emerald-900' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:shadow-indigo-50'}`}>
                          <input type="checkbox" checked={item.completed} disabled={!canModifyChecklist} onChange={() => toggleChecklistItem(item.id)} className="w-6 h-6 rounded-lg border-slate-300 text-indigo-600" />
                          <span className="font-bold text-lg flex-1 leading-snug tracking-tight italic">{item.label}</span>
                          {item.completed ? <CheckCircle className="w-6 h-6 text-emerald-500" /> : <div className="w-6 h-6 rounded-full border-2 border-slate-100 group-hover:border-indigo-100"></div>}
                        </label>
                      ))}
                    </div>

                    {selectedViewStage === project.stage && project.stage === ProjectStage.DEVELOPMENT && ((currentUser?.role as string) === UserRole.DEV_MANAGER || isAdmin) && (
                      <div className="bg-red-50/50 p-8 rounded-[2.5rem] border border-red-100 space-y-5">
                        <div className="flex items-center gap-3">
                           <RotateCcw className="w-5 h-5 text-red-600" />
                           <h4 className="text-sm font-black text-red-900 uppercase tracking-widest italic">Send back to Designer</h4>
                        </div>
                        <textarea placeholder="Type reason for return..." className="w-full p-4 bg-white border border-red-200 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-red-500 focus:outline-none min-h-[100px] shadow-inner" value={regressionNote} onChange={(e) => setRegressionNote(e.target.value)} />
                        <button onClick={handleDevToDesignRegression} disabled={!regressionNote.trim()} className="w-full py-4 bg-red-600 text-white font-black rounded-xl uppercase tracking-widest text-[10px]">Send back to Designer</button>
                      </div>
                    )}

                    {selectedViewStage === project.stage && project.stage === ProjectStage.QA && ((currentUser?.role as string) === UserRole.QA_ENGINEER || isAdmin) && (
                      <div className="flex gap-6 pt-6">
                        <button onClick={() => recordQAFeedback(project.id, true, currentUser?.id || '')} disabled={!isAdmin && !isChecklistComplete} className="flex-1 py-5 bg-emerald-600 text-white font-black rounded-3xl shadow-xl uppercase tracking-widest text-[10px]">PASSED QUALITY PROTOCOL</button>
                        <button onClick={() => recordQAFeedback(project.id, false, currentUser?.id || '')} className="flex-1 py-5 bg-red-600 text-white font-black rounded-3xl shadow-xl uppercase tracking-widest text-[10px]">REJECTED / RETURN TO DEV</button>
                      </div>
                    )}

                    {selectedViewStage === project.stage && (project.stage === ProjectStage.SEND_TO_CLIENT || project.stage === ProjectStage.SENT_TO_CLIENT) && isAdmin && (
                      <div className="space-y-4 pt-6">
                        {project.stage === ProjectStage.SEND_TO_CLIENT ? (
                          <button onClick={handleAdvanceAction} disabled={!isAdmin && !isChecklistComplete} className="w-full py-7 bg-orange-600 text-white font-black rounded-[2.5rem] text-lg uppercase tracking-[0.3em] italic">Submit to Client</button>
                        ) : (
                          <div className="grid grid-cols-2 gap-6">
                            <button onClick={handleRegressAction} className="py-6 bg-slate-800 text-white font-black rounded-3xl uppercase tracking-widest text-[10px]">Request Revisions</button>
                            <button onClick={handleAdvanceAction} className="py-6 bg-emerald-600 text-white font-black rounded-3xl uppercase tracking-widest text-[10px]">Close Project</button>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedViewStage === project.stage && 
                     (project.stage === ProjectStage.DESIGN || project.stage === ProjectStage.DEVELOPMENT) && (
                      <button 
                        onClick={handleAdvanceAction} 
                        disabled={(!isAdmin && (project.stage === ProjectStage.DESIGN ? false : !isChecklistComplete)) || !canModifyChecklist} 
                        className="w-full py-7 bg-indigo-600 text-white font-black rounded-[2.5rem] text-xl uppercase tracking-[0.3em] italic"
                      >
                        <Send className="w-6 h-6" /> Submit Phase
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm">
                <div className="relative border-l-4 border-slate-100 ml-6 pl-12 space-y-12">
                  {project.history.map((h, i) => (
                    <div key={i} className="relative">
                      <div className="absolute -left-[54px] top-0 w-6 h-6 rounded-full bg-white border-4 border-indigo-600"></div>
                      <div className="flex items-center gap-3 mb-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        <span>{format(new Date(h.timestamp), 'MMM d, h:mm a')}</span>
                        <span>•</span>
                        <span className="text-indigo-600">{users.find(u => u.id === h.userId)?.name}</span>
                      </div>
                      <p className="font-black text-slate-900 text-lg tracking-tight italic">{h.action}</p>
                      {h.note && <p className="mt-2 text-sm text-indigo-900 bg-indigo-50 p-3 rounded-lg italic">"{h.note}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="w-[440px] flex flex-col relative z-20"><ProjectCommentsSidebar project={project} /></div>
      </div>
    </div>
  );
};
